import cns from "classnames";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useClickAway, useKeyPressEvent } from "react-use";

import pages from "@Settings/pages";
import Link from "@Components/ui/Link";
import TextIcon from "@Components/ui/TextIcon";

import styles from "./index.module.scss";

export type NavProps = {
    className?: string;
};

type Links = {
    name: string;
    href: string;
}[];

const links: Links = [
    {
        name: pages.delivery.name,
        href: pages.delivery.link,
    },
    {
        name: pages.refund.name,
        href: pages.refund.link,
    },
    {
        name: pages.payment.name,
        href: pages.payment.link,
    },
    {
        name: pages.sizeGuide.name,
        href: pages.sizeGuide.link,
    },
    {
        name: pages.trust.name,
        href: pages.trust.link,
    },
    {
        name: pages.feedback.name,
        href: pages.feedback.link,
    },
    {
        name: pages.faq.name,
        href: pages.faq.link,
    },
    {
        name: pages.offer.name,
        href: pages.offer.link,
    },
];

const Nav: React.FC<NavProps> = ({ className }) => {
    const router = useRouter();

    const refComponent = useRef<HTMLDivElement>(null);

    const [listIsOpen, setListIsOpen] = useState<boolean>(false);

    useEffect(() => {
        setListIsOpen(false);
    }, [router.asPath]);

    useClickAway(refComponent, () => {
        setListIsOpen(false);
    });

    useKeyPressEvent("Escape", () => setListIsOpen(false));

    const handleButtonDropdownClick = () => {
        setListIsOpen(prev => !prev);
    };

    return (
        <div
            ref={refComponent}
            className={cns(className, styles.component, "text text_p3", {
                isOpen: listIsOpen,
            })}
        >
            <button
                className={cns(
                    styles.component__buttonDropdown,
                    "notForDesktop"
                )}
                type="button"
                onClick={handleButtonDropdownClick}
            >
                <TextIcon
                    textClassName={styles.component__buttonDropdownText}
                    icon="angle-down"
                    isRight
                >
                    {links.find(link => link.href === router.route)?.name}
                </TextIcon>
            </button>

            <div className={styles.component__list}>
                {links.map((link, linkIndex) => (
                    <Link
                        key={linkIndex}
                        className={cns(styles.component__link, {
                            isActive: router.route === link.href,
                        })}
                        href={link.href}
                    >
                        {link.name}
                    </Link>
                ))}
            </div>
        </div>
    );
};

export default Nav;
